import { useEffect, useState } from "react";
import { AdminThumb } from "@/components/admin/AdminThumb";
import axios from "axios";
import { FilmStrip, ListChecks, Leaf, Globe, ClockCounterClockwise, ArrowClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n ?? 0).toLocaleString();

const Card = ({ testid, icon: Icon, label, value, sub, accent, children }) => (
  <div data-testid={testid} className="rv-stat-card relative overflow-hidden border border-white/10 bg-[#0A0A0A] p-5">
    <div className={`absolute inset-x-0 top-0 h-[2px] ${accent}`} />
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
      <Icon size={16} weight="duotone" /> {label}
    </div>
    {value != null && <p data-testid={`${testid}-value`} className="mt-3 font-serif text-4xl tracking-tight text-[#EDEDED]">{value}</p>}
    {sub && <p className="mt-1 font-mono text-[10px] text-white/35">{sub}</p>}
    {children}
  </div>
);

export const DashboardStats = ({ headers }) => {
  const [s, setS] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/dashboard-stats`, { headers, withCredentials: true }).then(({ data }) => setS(data)).catch(() => setS({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div data-testid="dashboard-stats" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Card testid="stat-active-videos" icon={FilmStrip} label="Total Active Videos" value={s ? fmt(s.active_videos) : "…"} sub="Published and playable across all rows" accent="bg-amber" />
      <Card testid="stat-needs-review" icon={ListChecks} label="Needs Review Queue" value={s ? fmt(s.needs_review) : "…"} sub="Films waiting for a genre" accent="bg-crimson" />
      <Card testid="stat-dispensary" icon={Leaf} label="Affiliate Conversions" value={s ? fmt(s.dispensary_clicks) : "…"}
        sub={s ? `🍁 Dispensary clicks · ${fmt(s.dispensary_unique)} unique visitors` : ""} accent="bg-emerald-400" />
      <Card testid="stat-referrals" icon={Globe} label="Referral Traffic · 30d" accent="bg-sky-400"
        sub={s ? `${fmt(s.visits_30d)} visit${s.visits_30d === 1 ? "" : "s"} · ${fmt(s.direct_30d)} direct` : ""}>
        <ol data-testid="stat-referrals-list" className="mt-3 space-y-1.5">
          {(s?.top_sources || []).map((t, i) => (
            <li key={t.source} className="flex items-baseline gap-2 text-sm text-white/80">
              <span className="font-mono text-[10px] text-sky-300">{i + 1}</span>
              <span className="truncate">{t.source}</span>
              <span className="ml-auto font-mono text-[11px] text-white/45">{fmt(t.visits)}</span>
            </li>
          ))}
          {s && !s.top_sources?.length && <li className="font-mono text-[11px] text-white/35">No outside referrals recorded yet</li>}
        </ol>
      </Card>
    </div>
  );
};

export const ImportHistory = ({ headers }) => {
  const [items, setItems] = useState(null);
  const load = () =>
    axios.get(`${API}/admin/import-history`, { params: { limit: 10 }, headers, withCredentials: true })
      .then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div data-testid="import-history" className="border border-white/10 bg-[#0A0A0A]">
      <div className="flex items-center gap-3 px-6 py-5">
        <ClockCounterClockwise size={22} weight="duotone" className="text-amber" />
        <h2 className="font-serif text-xl tracking-tight text-[#EDEDED]">Import History</h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">Last 10 runs</span>
        <button type="button" data-testid="import-history-refresh" onClick={load} className="ml-auto flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60 hover:border-amber hover:text-amber transition-colors duration-300">
          <ArrowClockwise size={13} weight="bold" /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto border-t border-white/10">
        <table className="w-full min-w-[640px] text-left">
          <thead className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
            <tr>{["Thumbnail", "When", "Tool", "Source query", "Genre", "Pulled"].map((h) => <th key={h} className="px-6 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(items || []).map((it, i) => (
              <tr key={`${it.created_at}-${i}`} data-testid={`import-history-row-${i}`} className="border-t border-white/5 text-[13px] text-white/75">
                <td className="py-2 pl-6 pr-2" title={it.sample?.title || ""}>
                  <AdminThumb url={it.sample?.url || it.query} thumb={it.sample?.thumb} testid={`import-history-thumb-${i}`} />
                </td>
                <td className="whitespace-nowrap px-6 py-3 font-mono text-[11px] text-white/50">{new Date(it.created_at).toLocaleString()}</td>
                <td className="whitespace-nowrap px-6 py-3">{it.tool}</td>
                <td className="max-w-[320px] truncate px-6 py-3 text-white/60" title={it.query}>{it.query || "—"}</td>
                <td className="whitespace-nowrap px-6 py-3 text-white/60">{it.genre || "—"}</td>
                <td className="px-6 py-3">
                  <span className={`font-mono ${it.pulled ? "text-amber" : "text-white/40"}`}>{fmt(it.pulled)}</span>
                  {it.errors?.length > 0 && <span className="ml-2 font-mono text-[10px] text-crimson" title={it.errors.join("\n")}>{it.errors.length} issue{it.errors.length > 1 ? "s" : ""}</span>}
                  {it.skipped && (
                    <span data-testid={`import-history-skipped-${i}`} className="ml-2 font-mono text-[10px] text-white/35"
                      title={Object.entries(it.skipped).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join("\n")}>
                      {Object.entries(it.skipped).filter(([k]) => k !== "already_in_vault").reduce((a, [, v]) => a + v, 0)} skipped
                      {it.skipped.already_in_vault ? ` · ${it.skipped.already_in_vault} already in vault` : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {items && !items.length && (
              <tr><td colSpan={6} className="px-6 py-6 font-mono text-[11px] text-white/35">No imports recorded yet — runs appear here as soon as any import tool finishes.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
